import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { startProductAnalytics } from './product-analytics';
import { UpdateBar } from './kit/UpdateBar';
import { reloadForUpdate, resumeAfterUpdate, startUpdateChecks, useUpdate } from './update';
import { loadSession, sessionMayResume } from './api';
import { surface } from './surface';
import { primeTheme } from './theme';
import { primePrerendered } from './prerendered';
import { reportPageError, startErrorReporting } from './error-reporting';
import { PageCrash } from './kit/PageCrash';
// The design system's token layer. It comes FIRST on purpose: it is nine files of custom
// properties plus a `base.css` that paints the body ivory, and the app's own stylesheet is
// still the dark one the un-ported prose pages are built for. Importing it after lets those
// rules win, so this adds the tokens without repainting anything not yet ported. When the
// last page is ported, `styles.css` goes and this becomes the base.
import './design/styles.css';
import './styles.css';
import './kit/kit.css';
// The same tokens under other values, for the dashboard's dark theme (`theme.ts`). After the kit,
// which is what it re-colours.
import './kit/dark.css';

/**
 * The notice that a newer build is deployed (`update.ts`), above whatever the route drew.
 *
 * Here rather than inside `App`, which returns a different frame for nearly every route: a sibling
 * ahead of all of them is the one place that is on every screen, signed in or not.
 */
function UpdateNotice() {
  const update = useUpdate();
  if (!update.available) return null;
  return <UpdateBar note={update.note} reloading={update.reloading} onReload={reloadForUpdate} />;
}

// Before the first render, because a reload for an update that came back on the build it left goes
// straight out again, and there is nothing worth drawing on the way.
if (resumeAfterUpdate()) {
  // Before anything is drawn: a dark dashboard opens dark (`theme.ts`). Only at an address the
  // dashboard itself draws, and only where somebody may be signed in to it.
  const { pathname } = window.location;
  const dashboard =
    ['/teams', '/games', '/account'].some((root) => pathname === root || pathname.startsWith(`${root}/`)) ||
    (pathname === '/' && surface() === 'create');
  primeTheme(dashboard && (loadSession() !== null || sessionMayResume()));
  // A page the build wrote out as HTML (`prerendered.ts`) first renders with what it was written
  // with, so the live page that replaces it says the same thing.
  primePrerendered();
  startErrorReporting();
  startProductAnalytics();
  startUpdateChecks();
  // A render that throws is React's to catch, and it never reaches `window`'s listeners: these two are
  // where it is told. Logged as React would have logged it without them.
  const renderFailed = (error: unknown) => {
    console.error(error);
    reportPageError('render', error);
  };
  createRoot(document.getElementById('root')!, {
    onCaughtError: renderFailed,
    onUncaughtError: renderFailed,
  }).render(
    <StrictMode>
      <UpdateNotice />
      <PageCrash>
        <App />
      </PageCrash>
    </StrictMode>,
  );
}
